declare global {
  interface Window {
    goog?: any;
  }
}

const PAL_SDK_URL =
  "https://imasdk.googleapis.com/pal/sdkloader/pal.js";

let _nonceLoader: any | undefined;
let _palLoadPromise: Promise<void> | undefined;

function loadPalSdk(): Promise<void> {
  if (typeof window !== "undefined" && window.goog?.pal) {
    return Promise.resolve();
  }

  if (_palLoadPromise) return _palLoadPromise;

  _palLoadPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector(
      'script[data-pal="true"]'
    ) as HTMLScriptElement | null;

    if (existing) {
      if (window.goog?.pal) {
        resolve();
        return;
      }
      const timeout = setTimeout(
        () => reject(new Error("PAL SDK load timeout")),
        10000
      );
      existing.addEventListener("load", () => {
        clearTimeout(timeout);
        resolve();
      });
      existing.addEventListener("error", () => {
        clearTimeout(timeout);
        reject(new Error("PAL SDK load failed"));
      });
      return;
    }

    const script = document.createElement("script");
    script.src = PAL_SDK_URL;
    script.async = true;
    script.setAttribute("data-pal", "true");
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("PAL SDK load failed"));
    document.head.appendChild(script);
  });

  return _palLoadPromise;
}

function getOrCreateNonceLoader(): any {
  if (_nonceLoader) return _nonceLoader;
  const goog = window.goog;
  const consentSettings = new goog.pal.ConsentSettings();
  consentSettings.allowStorage = true;
  _nonceLoader = new goog.pal.NonceLoader(consentSettings);
  return _nonceLoader;
}

export interface PalNonceOptions {
  videoWidth?: number;
  videoHeight?: number;
  descriptionUrl?: string;
  ppid?: string;
  sessionId?: string;
  adWillAutoPlay?: boolean;
  adWillPlayMuted?: boolean;
  playerVolume?: number;
  continuousPlayback?: boolean;
}

export interface PalNonceManager {
  generateNonce(options?: PalNonceOptions): Promise<string>;

  getCurrentNonce(): string;

  injectNonce(vastTagUrl: string): string;

  sendPlaybackStart(): void;

  sendPlaybackEnd(): void;

  sendAdClick(): void;

  sendAdTouch(event: MouseEvent | TouchEvent): void;

  reset(): void;
}

export function createPalNonceManager(): PalNonceManager {
  let currentNonce = "";
  let currentNonceManager: any | undefined;

  return {
    async generateNonce(options: PalNonceOptions = {}): Promise<string> {
      try {
        await loadPalSdk();

        const loader = getOrCreateNonceLoader();
        const goog = window.goog;

        const request = new goog.pal.NonceRequest();
        request.adWillAutoPlay = options.adWillAutoPlay ?? true;
        request.adWillPlayMuted = options.adWillPlayMuted ?? false;
        request.playerVolume =
          options.playerVolume ?? (request.adWillPlayMuted ? 0 : 1);
        request.continuousPlayback = options.continuousPlayback ?? true;
        request.descriptionUrl =
          options.descriptionUrl ||
          (typeof window !== "undefined" ? window.location.href : "");
        request.iconsSupported = true;
        request.playerType = "StormcloudVideoPlayer";
        request.playerVersion = "1.0";
        request.supportedApiFrameworks = "2,7,9";
        request.url =
          options.descriptionUrl ||
          (typeof window !== "undefined" ? window.location.href : "");
        request.videoWidth = options.videoWidth || 640;
        request.videoHeight = options.videoHeight || 480;

        if (options.ppid) request.ppid = options.ppid;
        if (options.sessionId) request.sessionId = options.sessionId;

        const manager = await loader.loadNonceManager(request);
        currentNonceManager = manager;
        currentNonce = manager.getNonce() as string;

        console.log("[PAL] Nonce generated successfully");
        return currentNonce;
      } catch (error) {
        console.warn("[PAL] Failed to generate nonce:", error);
        currentNonce = "";
        currentNonceManager = undefined;
        return "";
      }
    },

    getCurrentNonce(): string {
      return currentNonce;
    },

    injectNonce(vastTagUrl: string): string {
      if (!currentNonce || !vastTagUrl) return vastTagUrl;

      try {
        const url = new URL(vastTagUrl);
        url.searchParams.delete("paln");
        url.searchParams.set("givn", currentNonce);
        return url.toString();
      } catch {
        let base = vastTagUrl
          .replace(/([?&])paln=[^&]*/g, "")
          .replace(/[?&]$/, "");
        const sep = base.includes("?") ? "&" : "?";
        return `${base}${sep}givn=${encodeURIComponent(currentNonce)}`;
      }
    },

    sendPlaybackStart(): void {
      try {
        currentNonceManager?.sendPlaybackStart();
      } catch {}
    },

    sendPlaybackEnd(): void {
      try {
        currentNonceManager?.sendPlaybackEnd();
      } catch {}
    },

    sendAdClick(): void {
      try {
        currentNonceManager?.sendAdClick();
      } catch {}
    },

    sendAdTouch(event: MouseEvent | TouchEvent): void {
      try {
        currentNonceManager?.sendAdTouch(event);
      } catch {}
    },

    reset(): void {
      currentNonce = "";
      currentNonceManager = undefined;
    },
  };
}
